import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import './GeneratorControlBar.css';

// Peek handle title label: phones stay sparkles-only. Full label only near
// tablet widths at the upper end of the mobile sheet breakpoint (~62.4rem).
const SHEET_TITLE_LABEL_MIN_ROW_REM = 52;

const TXT_CLOSE_PANEL = {
    fr: 'Fermer le panneau du générateur',
    en: 'Close generator panel',
    de: 'Generator-Panel schließen',
    es: 'Cerrar el panel del generador',
    pt: 'Fechar o painel do gerador',
};

const GeneratorControlBar = ({
    variant = 'generator',
    isBusy = false,
    isBlocked = false,
    isCollapsed = false,
    showGeneratorControls = true,
    displayModeToggle = null,
    displayModeTitle = null,
    titleContent = null,
    metaContent = null,
    timerContent = null,
    busyStatusContent = null,
    genModeControls = null,
    settingsContent = null,
    criteriaContent = null,
    scoreContent = null,
    runControlContent = null,
    sheetRunStatusContent = null,
    sheetRunButtonContent = null,
    showScore = true,
    className = '',
    // Mobile bottom-sheet (generator variant): collapsed-peek summary node
    // (sparkles + criteria icons) and the expanded per-criterion list node.
    sheetSummaryContent = null,
    sheetTimerContent = null,
    sheetDefenseContent = null,
    sheetFmContent = null,
    sheetScoreContent = null,
    sheetCriteriaContent = null,
    sheetSettingsContent = null,
}) => {
    const { lang } = useParams();
    const isAiChat = variant === 'aichat';
    // Mobile only: the bar becomes a bottom sheet that peeks above the nav and
    // expands on a vertical drag of the handle. The state is inert on desktop
    // (no CSS hooks into it there).
    const [sheetOpen, setSheetOpen] = useState(false);
    // Peek title: full label when there's room, sparkles-only when cramped.
    const [sheetTitleCompact, setSheetTitleCompact] = useState(false);
    // Draggable bottom sheet: the panel height follows the finger between its
    // peek (collapsed) and full (open) heights, then snaps to whichever it's
    // closest to on release. Pointer events cover both touch and mouse. We drive
    // the height with an inline max-height during the drag and hand control back
    // to the CSS class (with its transition) once the gesture settles.
    const barRef = useRef(null);
    const handleRowRef = useRef(null);
    const sheetDrag = useRef(null);

    const measureSheetBounds = (el) => {
        const handle = el.querySelector('.generator-bottom-bar__sheet-handle');
        const inner = el.querySelector('.generator-bottom-bar__inner');
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        // Snap the collapsed target to the CSS peek height (--sheet-peek), NOT the
        // handle's own height. The handle is intentionally taller than the peek
        // (its bottom divider is tucked out of view), so using its height would
        // land above the resting height and trigger a second CSS settle once the
        // inline override is cleared — a visible extra downward glide on close.
        const peekRaw = getComputedStyle(el).getPropertyValue('--sheet-peek').trim();
        const peekVarPx = peekRaw.endsWith('px') ? parseFloat(peekRaw)
            : peekRaw.endsWith('rem') ? parseFloat(peekRaw) * rem
            : parseFloat(peekRaw);
        const peekPx = Number.isFinite(peekVarPx) ? peekVarPx : (handle ? handle.offsetHeight : 3.25 * rem);
        const openCap = Math.min(window.innerHeight * 0.8, 34 * rem);
        const contentPx = (handle ? handle.offsetHeight : 0) + (inner ? inner.scrollHeight : 0);
        const openPx = Math.max(peekPx + 1, Math.min(contentPx, openCap));
        return { peekPx, openPx };
    };

    const clearSheetDragStyles = () => {
        const el = barRef.current;
        if (!el) return;
        el.style.transition = '';
        el.style.maxHeight = '';
    };

    const animateSheetTo = (shouldOpen) => {
        const el = barRef.current;
        if (!el) return;
        const { peekPx, openPx } = measureSheetBounds(el);
        const targetPx = shouldOpen ? openPx : peekPx;
        const startHeight = el.getBoundingClientRect().height;
        el.style.transition = 'none';
        el.style.maxHeight = `${startHeight}px`;
        void el.offsetHeight;
        el.style.transition = '';
        el.style.maxHeight = `${targetPx}px`;
        setSheetOpen(shouldOpen);
    };

    const toggleSheet = () => {
        animateSheetTo(!sheetOpen);
    };

    const closeSheet = () => {
        if (sheetOpen) {
            animateSheetTo(false);
            return;
        }
        clearSheetDragStyles();
        setSheetOpen(false);
    };

    const onSheetHandlePointerDown = (e) => {
        const el = barRef.current;
        if (!el) return;
        const { peekPx, openPx } = measureSheetBounds(el);
        const startHeight = el.getBoundingClientRect().height;
        sheetDrag.current = {
            pointerId: e.pointerId,
            startY: e.clientY,
            startHeight,
            peekPx,
            openPx,
            live: startHeight,
            moved: false,
        };
        // Follow the finger instantly — no easing while dragging.
        el.style.transition = 'none';
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onSheetHandlePointerMove = (e) => {
        const drag = sheetDrag.current;
        const el = barRef.current;
        if (!drag || !el || e.pointerId !== drag.pointerId) return;
        if (Math.abs(e.clientY - drag.startY) > 3) drag.moved = true;
        const dragged = drag.startHeight + (drag.startY - e.clientY); // up grows height
        const next = Math.max(drag.peekPx, Math.min(drag.openPx, dragged));
        drag.live = next;
        el.style.maxHeight = `${next}px`;
    };

    const onSheetHandlePointerUp = (e) => {
        const drag = sheetDrag.current;
        const el = barRef.current;
        if (!drag) return;
        e.currentTarget.releasePointerCapture?.(drag.pointerId);
        sheetDrag.current = null;
        if (!el) return;
        if (!drag.moved) {
            // Tap with no travel: toggle open/closed (whole peek handle is the
            // hit target; nested gen/stop still stopPropagation so they don't
            // reach here).
            clearSheetDragStyles();
            toggleSheet();
            return;
        }
        const shouldOpen = drag.live > (drag.peekPx + drag.openPx) / 2;
        animateSheetTo(shouldOpen);
    };

    const onSheetTransitionEnd = (e) => {
        // Only the sheet's own max-height settle should drop the inline override;
        // ignore transitions bubbling up from child controls.
        if (e.target === barRef.current && e.propertyName === 'max-height' && !sheetDrag.current) {
            clearSheetDragStyles();
        }
    };

    // Mobile: collapse the sheet as soon as a generation run starts so the
    // progress fill in the peek handle is the focus.
    useEffect(() => {
        if (!isBusy) return;
        const el = barRef.current;
        if (!el) {
            setSheetOpen(false);
            return;
        }
        // Desktop: the sheet handle is display:none (no peek/sheet UI at all), so
        // there's nothing to collapse. Without this guard, measureSheetBounds falls
        // back to the hidden handle's offsetHeight (0) and the max-height hack below
        // collapses the whole sticky bar's box to 0, leaving its content overflowing
        // past the box with no overflow:hidden to contain it on desktop.
        const handle = el.querySelector('.generator-bottom-bar__sheet-handle');
        if (!handle || getComputedStyle(handle).display === 'none') {
            setSheetOpen(false);
            return;
        }
        const { peekPx } = measureSheetBounds(el);
        const startHeight = el.getBoundingClientRect().height;
        if (startHeight <= peekPx + 1) {
            clearSheetDragStyles();
            setSheetOpen(false);
            return;
        }
        el.style.transition = 'none';
        el.style.maxHeight = `${startHeight}px`;
        void el.offsetHeight;
        el.style.transition = '';
        el.style.maxHeight = `${peekPx}px`;
        setSheetOpen(false);
    }, [isBusy]);

    const showSheetHandleRun = showGeneratorControls && !!sheetRunButtonContent;
    const showSheetHandleProgress = isBusy && !!sheetRunStatusContent;
    const showSheetHandleTitle = displayModeTitle || displayModeToggle;
    // Progress label owns the peek center while busy — hide criteria chips.
    const showSheetSummary = !!sheetSummaryContent && !isBusy;

    // Compact the peek title to sparkles-only except on wide tablet peeks.
    useLayoutEffect(() => {
        if (isAiChat) return undefined;
        const row = handleRowRef.current;
        if (!row || !showSheetHandleTitle || !displayModeTitle) {
            setSheetTitleCompact(false);
            return undefined;
        }
        const recompute = () => {
            const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
            setSheetTitleCompact(row.clientWidth < SHEET_TITLE_LABEL_MIN_ROW_REM * rem);
        };
        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(row);
        return () => ro.disconnect();
    }, [isAiChat, showSheetHandleTitle, displayModeTitle]);

    const rootClassName = [
        'generator-bottom-bar',
        isAiChat ? 'generator-bottom-bar--aichat' : '',
        isBusy ? 'generator-bottom-bar--busy' : '',
        isBlocked ? 'generator-bottom-bar--blocked' : '',
        !showGeneratorControls ? 'generator-bottom-bar--simple' : '',
        isCollapsed ? 'generator-bottom-bar--collapsed' : '',
        sheetOpen ? 'generator-bottom-bar--sheet-open' : '',
        sheetTitleCompact ? 'generator-bottom-bar--sheet-title-compact' : '',
        className,
    ].filter(Boolean).join(' ');
    const leadingContent = titleContent || displayModeToggle;
    const aiBusyStatusContent = isBusy && busyStatusContent ? busyStatusContent : null;

    const stopSheetControlPointer = (e) => e.stopPropagation();

    const renderDivider = (key) => (
        <span key={key} className={`generator-bottom-bar__divider generator-bottom-bar__divider--${key}`} aria-hidden="true" />
    );

    const renderControlsRule = (key) => (
        <span key={key} className={`generator-bottom-bar__controls-rule generator-bottom-bar__controls-rule--${key}`} aria-hidden="true" />
    );

    const barTimerGenSettings = (
        <>
            {timerContent ? (
                <div className="generator-bottom-bar__timer">
                    {timerContent}
                </div>
            ) : null}
            {genModeControls ? (
                <div className="generator-bottom-bar__gen-mode">
                    {genModeControls}
                </div>
            ) : null}
            {settingsContent ? (
                <div className="generator-bottom-bar__settings">
                    {settingsContent}
                </div>
            ) : null}
        </>
    );

    const barCriteriaScoreGroup = (
        <div className="generator-bottom-bar__criteria-score-group">
            <div className="generator-bottom-bar__criteria">
                {criteriaContent}
            </div>
            {showScore && scoreContent ? (
                <>
                    {renderDivider('criteria-score-divider')}
                    <div className="generator-bottom-bar__score">
                        {scoreContent}
                    </div>
                </>
            ) : null}
        </div>
    );

    if (isAiChat) {
        return (
            <div className={rootClassName}>
                <div className="generator-bottom-bar__inner row">
                    <div className="generator-bottom-bar__main-row row">
                        {leadingContent ? (
                            <div className="generator-bottom-bar__display-mode">
                                {leadingContent}
                            </div>
                        ) : null}
                        {leadingContent ? renderDivider('display-divider') : null}
                        {timerContent ? (
                            <div className="generator-bottom-bar__timer">
                                {timerContent}
                            </div>
                        ) : null}
                        {metaContent ? (
                            <div className="generator-bottom-bar__meta">
                                {metaContent}
                            </div>
                        ) : null}
                        {genModeControls ? (
                            <div className="generator-bottom-bar__gen-mode">
                                {genModeControls}
                            </div>
                        ) : null}
                        {settingsContent ? (
                            <div className="generator-bottom-bar__settings">
                                {settingsContent}
                            </div>
                        ) : null}
                        {runControlContent ? renderDivider('after-settings-divider') : null}
                        {runControlContent ? (
                            <div className="generator-bottom-bar__action-group">
                                {aiBusyStatusContent ? (
                                    <div className="generator-bottom-bar__timer generator-bottom-bar__timer--status-slot">
                                        {aiBusyStatusContent}
                                    </div>
                                ) : null}
                                {runControlContent}
                            </div>
                        ) : null}
                    </div>
                    {criteriaContent ? (
                        <div className="generator-bottom-bar__criteria-row">
                            {barCriteriaScoreGroup}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <>
            {sheetOpen ? (
                <button
                    type="button"
                    className="generator-bottom-bar__backdrop"
                    onClick={closeSheet}
                    aria-label={TXT_CLOSE_PANEL[lang] || TXT_CLOSE_PANEL.en}
                />
            ) : null}
            <div className={rootClassName} ref={barRef} onTransitionEnd={onSheetTransitionEnd}>
                {/* Mobile only: peek handle docked above the nav — drag up to expand
                    the sheet into the full controls below, drag down to collapse.
                    Hidden on desktop. */}
                <div
                    className="generator-bottom-bar__sheet-handle"
                    role="button"
                    tabIndex={0}
                    onPointerDown={onSheetHandlePointerDown}
                    onPointerMove={onSheetHandlePointerMove}
                    onPointerUp={onSheetHandlePointerUp}
                    onPointerCancel={onSheetHandlePointerUp}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSheet();
                        }
                    }}
                    aria-expanded={sheetOpen}
                    aria-label={sheetOpen ? 'Close generator panel' : 'Open generator panel'}
                >
                    {showSheetHandleProgress ? (
                        <span
                            className="generator-bottom-bar__sheet-progress"
                            onPointerDown={stopSheetControlPointer}
                        >
                            {sheetRunStatusContent}
                        </span>
                    ) : null}
                    <span className="generator-bottom-bar__grip" aria-hidden="true" />
                    <span className="generator-bottom-bar__sheet-handle-row" ref={handleRowRef}>
                        {showSheetHandleTitle && displayModeTitle ? (
                            <span className="generator-bottom-bar__sheet-title">
                                {displayModeTitle}
                            </span>
                        ) : showSheetHandleTitle && displayModeToggle ? (
                            <span
                                className="generator-bottom-bar__sheet-toggle"
                                onPointerDown={stopSheetControlPointer}
                            >
                                {displayModeToggle}
                            </span>
                        ) : null}
                        {showSheetSummary ? sheetSummaryContent : null}
                        {showSheetHandleRun ? (
                            <span
                                className="generator-bottom-bar__sheet-actions"
                                onPointerDown={stopSheetControlPointer}
                            >
                                {sheetRunButtonContent}
                            </span>
                        ) : null}
                    </span>
                </div>
                <div className="generator-bottom-bar__inner row">
                    {leadingContent ? (
                        <div className="generator-bottom-bar__display-mode">
                            {leadingContent}
                        </div>
                    ) : null}
                    {leadingContent ? renderDivider('display-divider') : null}
                    {showGeneratorControls ? (
                        <>
                            {renderControlsRule('top')}
                            {barTimerGenSettings}
                            {criteriaContent ? renderDivider('criteria-divider') : null}
                            {criteriaContent ? barCriteriaScoreGroup : null}
                            {/* Mobile only: the expanded sheet swaps the desktop
                                criteria chips for a per-criterion list. Hidden on
                                desktop via CSS. */}
                            {runControlContent && !sheetOpen ? (
                                <>
                                    {renderDivider(isBusy || isBlocked ? 'busy-status-divider' : 'generate-divider')}
                                    <div className="generator-bottom-bar__action-group">
                                        {runControlContent}
                                    </div>
                                </>
                            ) : null}
                            {renderControlsRule('bottom')}
                            {sheetTimerContent ? (
                                <div className="generator-bottom-bar__sheet-timer-row">
                                    {sheetTimerContent}
                                </div>
                            ) : null}
                            {sheetDefenseContent ? (
                                <div className="generator-bottom-bar__sheet-defense">
                                    {sheetDefenseContent}
                                </div>
                            ) : null}
                            {sheetFmContent ? (
                                <div className="generator-bottom-bar__sheet-fm">
                                    {sheetFmContent}
                                </div>
                            ) : null}
                            {sheetCriteriaContent ? (
                                <div className="generator-bottom-bar__sheet-criteria">
                                    {sheetCriteriaContent}
                                </div>
                            ) : null}
                            {sheetScoreContent ? (
                                <div className="generator-bottom-bar__sheet-score">
                                    {sheetScoreContent}
                                </div>
                            ) : null}
                            {sheetSettingsContent ? (
                                <div className="generator-bottom-bar__sheet-settings">
                                    {sheetSettingsContent}
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default GeneratorControlBar;
